import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Grid,
  Button,
  TextField,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Checkbox,
  ListItemText,
  CircularProgress,
  Autocomplete,
} from "@mui/material";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { AxiosResponse } from "axios";
import { updateUser, getRoles, getNetworkPartners, getCofUsers } from "redux/actions";
import { UserData } from "types/models/master/MasterApp";
import { AppState } from "redux/store";
import ChangePasswordForm from "../../ChangePasswordForm";
import CloseIcon from "@mui/icons-material/Close";
import { Man } from "@mui/icons-material";

interface UserUpdateProps {
  user: UserData;
}

interface FormValues {
  name: string;
  username: string;
  network_partners: string[];
  permissions: string[];
  roleId: number | string;
  contactNo: string;
  email: string;
  status: string;
  SRCode: string;
  managerId?: number;
}
const PERMISSION_OPTIONS = [
  { value: "report_download", label: "Report Download" },
  { value: "generate_lead", label: "Generate Lead" },
];
const UserUpdate: React.FC<UserUpdateProps> = ({ user }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const roles = useSelector((state: AppState) => state.master.roles || []);
  const networkPartners = useSelector((state: AppState) => state.master.networkPartners || []);
  const tempUser = useSelector((state: AppState) => state.master.cofUsers || []);
  const cofUsers = useMemo(
    () => tempUser.filter(u => String(u.id) !== String(user.id)),
    [tempUser, user?.id]
  );
  const [openDialog, setOpenDialog] = useState(false);
  const handleOpenDialog = () => setOpenDialog(true);
  const handleCloseDialog = () => setOpenDialog(false);

  useEffect(() => {
    dispatch(getRoles());
    dispatch(getNetworkPartners());
    dispatch(getCofUsers());
  }, [dispatch]);

  const mapPartnerNames = (partnerNamesCSV: string | null | undefined): string[] => {
    if (!partnerNamesCSV) return [];
    return partnerNamesCSV.split(",").map((n) => n.trim()).filter(Boolean);
  };
  const mapPermissionNames = (name: string | null | undefined): string[] => {
    if (!name) return [];
    return name.split(",").map((n) => n.trim()).filter(Boolean);
  };
  const initialValues: FormValues = {
    name: user?.name || "",
    username: user?.username || "",
    network_partners: mapPartnerNames(user?.networkPartnerCode),
    permissions: mapPermissionNames(user?.permission),
    roleId: user?.roleId ?? "",
    contactNo: user?.contactNo || "",
    email: user?.email || "",
    status: user?.IsActive ? "true" : "false",
    SRCode: user?.SRCode || "",
    managerId: cofUsers.some((u) => u.id === user?.managerId) ? user?.managerId : 0,
  };

  const validationSchema = Yup.object().shape({
    name: Yup.string().required("Name is required"),
    username: Yup.string().required("Username is required"),
    roleId: Yup.number().required("Role is required"),
    contactNo: Yup.string()
      .matches(/^\d{10}$/, "Contact Number must be exactly 10 digits")
      .required("Contact Number is required"),
    email: Yup.string().email("Invalid email").required("Email is required"),
    status: Yup.string()
      .oneOf(["true", "false"], "Status must be Active or Inactive")
      .required("Status is required"),
    SRCode: Yup.string().when("roleId", {
      is: (roleId: number) => {
        const selectedRole = roles.find((r) => r.id === Number(roleId));
        return selectedRole?.role === "COF Admin" || selectedRole?.role === "COF User";
      },
      then: (schema) => schema.required("SR Code is required"),
      otherwise: (schema) => schema.notRequired(),
    }),
    managerId: Yup.number().when("roleId", {
      is: (roleId: number) => {
        const selectedRole = roles.find((r) => r.id === roleId);
        return selectedRole?.role === "COF User";
      },
      then: (schema) => schema.moreThan(0, "Manager is required"),
      otherwise: (schema) => schema.notRequired(),
    }),
  });

  const handleSubmit = async (values: FormValues) => {
    const selectedRole = roles.find((r) => r.id === Number(values.roleId));
    const dataPayload: any = {
      name: values.name,
      username: values.username,
      roleId: +values.roleId,
      contactNo: values.contactNo,
      email: values.email,
      IsActive: values.status === "true",
      SRCode: values.SRCode,
    };

    if (selectedRole?.role === "COF User") {
      dataPayload.networkPartnerCode = values.network_partners.map((p) => p.trim()).join(",");
      dataPayload.permission = values.permissions.map((p) => p.trim()).join(",");
    } else {
      dataPayload.networkPartnerCode = null;
      dataPayload.permission = null;
    }
    if (selectedRole?.role === "COF User") {
      dataPayload.managerId = values.managerId;
    } else {
      dataPayload.managerId = null;
    }
    try {
      if (user) {
        const response = (await dispatch(
          updateUser(user.id, dataPayload)
        )) as unknown as AxiosResponse;
        if (response.data?.status === "success") {
          navigate("/master/manageUser");
        }
      }
    } catch (error) {
      console.error("Error updating user:", error);
    }
  };
  const partnerCodeToName = useMemo(() => {
    const map: Record<string, string> = {};
    networkPartners.forEach((p) => (map[p.partnerCode] = p.partnerName.trim()));
    return map;
  }, [networkPartners]);
  return (
    <Box sx={{ height: "100%", width: "100%", m: 8 }}>
      <Grid sx={{ display: "flex", flexDirection: "column", height: "100%", m: 2 }}>
        {networkPartners.length === 0 ? (
          <Box display="flex" justifyContent="center" mt={4}>
            <CircularProgress />
          </Box>
        ) : (
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
            enableReinitialize
          >
            {({ touched, errors, values, setFieldValue }) => {

              const selectedRole = roles.find((r) => r.id === Number(values.roleId));
              const showNetworkPartner = selectedRole?.moduleType === "COF";
              const showManager = selectedRole?.role === "COF User";

              return (
                <Form>
                  <Grid container spacing={3} mb={3}>
                    <Grid item xs={6}>
                      <Field
                        as={TextField}
                        fullWidth
                        label="Name"
                        name="name"
                        variant="outlined"
                        error={touched.name && !!errors.name}
                        helperText={<ErrorMessage name="name" />}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <Field
                        as={TextField}
                        fullWidth
                        label="Username"
                        name="username"
                        variant="outlined"
                        error={touched.username && !!errors.username}
                        helperText={<ErrorMessage name="username" />}
                      />
                    </Grid>
                  </Grid>

                  <Grid container spacing={3} mb={3}>
                    <Grid item xs={6}>
                      <Field name="roleId">
                        {({ field }: any) => (
                          <TextField
                            {...field}
                            select
                            fullWidth
                            label="Role Type"
                            variant="outlined"
                            error={touched.roleId && !!errors.roleId}
                            helperText={<ErrorMessage name="roleId" />}
                          >
                            {roles.map((role) => (
                              <MenuItem key={role.id} value={role.id}>
                                {role.role}
                              </MenuItem>
                            ))}
                          </TextField>
                        )}
                      </Field>
                    </Grid>
                    <Grid item xs={6}>
                      <Field
                        as={TextField}
                        fullWidth
                        label="Email"
                        name="email"
                        variant="outlined"
                        error={touched.email && !!errors.email}
                        helperText={<ErrorMessage name="email" />}
                      />
                    </Grid>
                  </Grid>

                  <Grid container spacing={3} mb={3}>
                    <Grid item xs={6}>
                      <Field
                        as={TextField}
                        fullWidth
                        label="Contact Number"
                        name="contactNo"
                        variant="outlined"
                        inputProps={{ maxLength: 10 }}
                        error={touched.contactNo && !!errors.contactNo}
                        helperText={<ErrorMessage name="contactNo" />}
                      />
                    </Grid>
                    <Grid item xs={3}>
                      <Field
                        as={TextField}
                        fullWidth
                        label="SR Code"
                        name="SRCode"
                        variant="outlined"
                        error={touched.SRCode && !!errors.SRCode}
                        helperText={<ErrorMessage name="SRCode" />}
                      />
                    </Grid>
                    <Grid item xs={3}>
                      <Field name="status">
                        {({ field }: any) => (
                          <TextField
                            {...field}
                            select
                            fullWidth
                            label="Status"
                            variant="outlined"
                            error={touched.status && !!errors.status}
                            helperText={<ErrorMessage name="status" />}
                          >
                            <MenuItem value="true">Active</MenuItem>
                            <MenuItem value="false">Inactive</MenuItem>
                          </TextField>
                        )}
                      </Field>
                    </Grid>
                  </Grid>

                  {showNetworkPartner && (
                    <Grid container spacing={3} mb={3}>
                      <Grid item xs={4}>
                        <Field name="network_partners">
                          {({ field, form: { setFieldValue }, meta: { touched, error } }: any) => (
                            <Autocomplete
                              multiple
                              disableCloseOnSelect
                              options={networkPartners}
                              getOptionLabel={(option: any) =>
                                `${option.partnerName?.trim() || ''} - ${option.partnerCode}`
                              }
                              value={networkPartners.filter((partner: any) =>
                                Array.isArray(field.value) && field.value.includes(partner.partnerCode)
                              )}
                              onChange={(event, selected: any[]) => {
                                const selectedCodes = selected.map((item) => item.partnerCode);
                                setFieldValue('network_partners', selectedCodes);
                              }}
                              isOptionEqualToValue={(option, value) =>
                                option.partnerCode === value.partnerCode
                              }
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label="Network Partners"
                                  error={touched.network_partners && !!error}
                                  helperText={<ErrorMessage name="network_partners" />}
                                />
                              )}
                            />
                          )}
                        </Field>
                      </Grid>

                      <Grid item xs={4}>
                        <Field name="permissions">
                          {({ field }: any) => (
                            <TextField
                              {...field}
                              fullWidth
                              select
                              label="Permissions"
                              value={values.permissions}
                              onChange={(e) => setFieldValue("permissions", e.target.value)}
                              error={touched.permissions && !!errors.permissions}
                              helperText={<ErrorMessage name="permissions" />}
                              SelectProps={{
                                multiple: true,
                                renderValue: (selected) => (selected as string[]).join(", "),
                                MenuProps: {
                                  PaperProps: {
                                    style: {
                                      maxHeight: 300,
                                    },
                                  },
                                },
                              }}
                            >
                              {PERMISSION_OPTIONS.map((permission) => {
                                const trimmedName = permission.label.trim();
                                return (
                                  <MenuItem key={permission.label} value={permission.label}>
                                    <Checkbox checked={values.permissions.includes(trimmedName)} />
                                    <ListItemText primary={trimmedName} />
                                  </MenuItem>
                                );
                              })}
                            </TextField>
                          )}
                        </Field>
                      </Grid>

                      {showManager && (
                        <Grid item xs={4}>
                          <Field name="managerId">
                            {({ field, form, meta }: any) => (
                              <Autocomplete
                                options={cofUsers}
                                getOptionLabel={(option: any) => option?.name?.trim() || ""}
                                value={
                                  cofUsers.find((u) => u.id === form.values.managerId) || null
                                }
                                onChange={(event, newValue) => {
                                  form.setFieldValue("managerId", newValue ? newValue.id : 0);
                                }}
                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    label="Manager"
                                    variant="outlined"
                                    error={meta.touched && Boolean(meta.error)}
                                    helperText={meta.touched && meta.error ? meta.error : ""}
                                  />
                                )}
                              />
                            )}
                          </Field>
                        </Grid>
                      )}
                    </Grid>
                  )}

                  <Box
                    sx={{
                      position: "fixed",
                      bottom: 0,
                      left: 0,
                      width: "100%",
                      display: "flex",
                      justifyContent: "flex-end",
                      gap: 2,
                      py: 2,
                      px: 4,
                      zIndex: 1000,
                      backgroundColor: "white",
                      boxShadow: "0 -2px 10px rgba(0, 0, 0, 0.1)",
                    }}
                  >
                    <Button variant="outlined" onClick={handleOpenDialog}>
                      Change Password
                    </Button>
                    <Button type="submit" variant="contained" color="primary">
                      {user ? "Update User" : "Add User"}
                    </Button>
                  </Box>
                </Form>
              );
            }}
          </Formik>
        )}

        <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
          <DialogTitle>
            Change Password
            <IconButton
              onClick={handleCloseDialog}
              sx={{ position: "absolute", right: 8, top: 8 }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <ChangePasswordForm onClose={handleCloseDialog} userId={user?.id} />
          </DialogContent>
        </Dialog>
      </Grid>
    </Box>
  );
};

export default UserUpdate;
